// PlaceDetail Component - Redesigned with GoodFactor and Pitch

import React, { useState, useEffect } from 'react';
import {
  ChevronLeft,
  Heart,
  MapPin,
  Clock,
  Star,
  Calendar,
  Users,
  Share2,
  Navigation,
  Phone,
  Mail,
  Globe,
  Sparkles,
  Palette,
  Sofa,
  Moon,
  Trees,
  UserCircle,
  MessageCircle,
  DoorOpen,
  Hammer,
  History,
  Ear,
  Coffee,
  Lightbulb,
  LucideIcon,
  BookOpen,
  X,
  Briefcase,
  // GoodWork Icons
  Target,
  Wrench,
  GraduationCap,
} from 'lucide-react';
import type { Place, PlaceEvent, Review, MainCategory, AnyCategory, GoodWorkCategory, WifiQuality, PowerOutletAvailability } from '../../types';
import { MAIN_CATEGORIES, GOODWORK_CATEGORIES, ALL_CATEGORIES, VIBE_TAGS } from '../../types';

// GoodWork helper functions for text-based display
const WIFI_LABELS: Record<WifiQuality, string> = {
  none: 'Kein WLAN',
  basic: 'WLAN verfügbar',
  fast: 'Schnelles WLAN',
  excellent: 'Sehr schnelles WLAN',
};

const POWER_LABELS: Record<PowerOutletAvailability, string> = {
  few: 'Wenige Steckdosen',
  some: 'Steckdosen vorhanden',
  many: 'Viele Steckdosen',
};
import { getMoodIcon, getActivityIcon, getCommunityIcon } from '../../constants';
import { getEventsForPlace } from '../../services/eventsService';
import { getReviewsForPlace } from '../../services/reviewsService';
import { ReviewSection } from '../reviews/ReviewSection';
import { ImageGallery } from './ImageGallery';
import { ValouBadges } from '../valou';
import { getPlaceValouAreas } from '../../services/valouService';
import { CheckInButton, CheckInList } from '../checkin';
import { SuggestChangesButton } from './SuggestChangesButton';
import { NoteEditor } from '../notes/NoteEditor';
import { PlaceMiniMap } from './PlaceMiniMap';
import { addPlaceNote } from '../../services/notesService';
import { useAuth } from '../../context/AuthContext';
import { isPlaceOpenNow } from '../../utils/openingHours';

// Icon mapping for categories and vibe tags
const ICON_MAP: Record<string, LucideIcon> = {
  // GoodLife Categories
  Palette,
  Sofa,
  Moon,
  Clock,
  Trees,
  // GoodWork Categories
  Users,
  Target,
  Calendar,
  Wrench,
  GraduationCap,
  // Vibe Tags
  UserCircle,
  MessageCircle,
  Sparkles,
  DoorOpen,
  Hammer,
  History,
  Ear,
  Coffee,
  Lightbulb,
};

// Color mapping for all categories - neutral gray for GoodLife, indigo for GoodWork
const CATEGORY_COLORS: Record<AnyCategory, { bg: string; text: string; border: string }> = {
  // GoodLife
  urban_creative: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' },
  public_living_room: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' },
  silent_sanctuary: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' },
  analoge_refugien: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' },
  natur_outdoor: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' },
  // GoodWork
  coworking: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300' },
  focused_work: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300' },
  creative_studio: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300' },
  meeting_space: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300' },
  maker_space: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300' },
  learning_hub: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300' },
};

interface PlaceDetailProps {
  place: Place;
  isFavorite: boolean;
  userId?: string;
  userLocation?: { latitude: number; longitude: number } | null;
  onBack: () => void;
  onToggleFavorite: (placeId: string) => void;
  onShowNotification?: (message: string, type: 'success' | 'info' | 'error') => void;
}

// GoodFactor Box Component (GoodLife)
function GoodFactorBox({ goodFactor }: { goodFactor: string }) {
  return (
    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-r-xl p-5 mb-6">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-5 h-5 text-green-600" />
        <h3 className="font-semibold text-green-800 uppercase tracking-wide text-sm">
          Das macht diesen Ort zum GoodPlace
        </h3>
      </div>
      <p className="text-gray-700 leading-relaxed">{goodFactor}</p>
    </div>
  );
}

// GoodWork Factor Box Component
function GoodWorkFactorBox({ goodworkFactor }: { goodworkFactor: string }) {
  return (
    <div className="bg-gradient-to-br from-indigo-50 to-violet-50 border-l-4 border-indigo-500 rounded-r-xl p-5 mb-6">
      <div className="flex items-center gap-2 mb-2">
        <Briefcase className="w-5 h-5 text-indigo-600" />
        <h3 className="font-semibold text-indigo-800 uppercase tracking-wide text-sm">
          Das macht diesen Ort zum GoodWork Place
        </h3>
      </div>
      <p className="text-gray-700 leading-relaxed">{goodworkFactor}</p>
    </div>
  );
}

// GoodWork Amenities Section - displays amenitiesText or falls back to structured data
function GoodWorkAmenitiesSection({ place }: { place: Place }) {
  const amenities = place.amenities;

  // Prefer amenitiesText if available
  if (place.amenitiesText) {
    return (
      <div className="border-b border-gray-200 pb-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Ausstattung und Möglichkeiten</h3>
        <p className="text-gray-700 whitespace-pre-line">{place.amenitiesText}</p>
      </div>
    );
  }

  // Fall back to structured amenities for backwards compatibility
  if (!amenities) return null;

  const items: string[] = [];

  if (amenities.wifi && amenities.wifi !== 'none') {
    items.push(WIFI_LABELS[amenities.wifi]);
  }
  if (amenities.powerOutlets) {
    items.push(POWER_LABELS[amenities.powerOutlets]);
  }
  if (amenities.monitors) items.push('Monitore');
  if (amenities.printer) items.push('Drucker');
  if (amenities.kitchen) items.push('Küche');
  if (amenities.meetingRooms) items.push('Meeting-Räume');
  if (amenities.phoneBooths) items.push('Telefonkabinen');
  if (amenities.locker) items.push('Schließfächer');

  if (items.length === 0) return null;

  return (
    <div className="border-b border-gray-200 pb-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-3">Ausstattung</h3>
      <p className="text-gray-700">{items.join(' · ')}</p>
    </div>
  );
}

// GoodWork Pricing Section
function GoodWorkPricingSection({ place }: { place: Place }) {
  const pricing = place.pricing;
  if (!pricing) return null;

  const priceItems: string[] = [];
  if (pricing.hourly) priceItems.push(`${pricing.hourly}€/Stunde`);
  if (pricing.daily) priceItems.push(`${pricing.daily}€/Tag`);
  if (pricing.monthly) priceItems.push(`${pricing.monthly}€/Monat`);

  const hasAnyContent = priceItems.length > 0 || pricing.dropIn || pricing.bookingUrl || pricing.priceNote;
  if (!hasAnyContent) return null;

  return (
    <div className="border-b border-gray-200 pb-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-3">Preise & Verfügbarkeit</h3>
      {priceItems.length > 0 && (
        <p className="text-gray-700 mb-2">{priceItems.join(' · ')}</p>
      )}
      {pricing.dropIn && (
        <p className="text-green-700 text-sm mb-2">Drop-in möglich (ohne Vorab-Buchung)</p>
      )}
      {pricing.priceNote && (
        <p className="text-gray-600 text-sm italic mb-3">{pricing.priceNote}</p>
      )}
      {pricing.bookingUrl && (
        <a
          href={pricing.bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block text-indigo-600 hover:text-indigo-800 font-medium text-sm hover:underline"
        >
          Jetzt buchen →
        </a>
      )}
    </div>
  );
}

// GoodWork Workspaces Section
function GoodWorkWorkspacesSection({ place }: { place: Place }) {
  const workspaces = place.workspaces;
  if (!workspaces) return null;

  const items: string[] = [];
  if (workspaces.totalSeats) items.push(`${workspaces.totalSeats} Plätze gesamt`);
  if (workspaces.quietZone) items.push('Ruhebereich');
  if (workspaces.teamArea) items.push('Teambereich');
  if (workspaces.standingDesks) items.push('Steharbeitsplätze');
  if (workspaces.outdoorSeats) items.push(`${workspaces.outdoorSeats} Plätze draußen`);

  if (items.length === 0) return null;

  return (
    <div className="border-b border-gray-200 pb-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-3">Arbeitsplätze</h3>
      <p className="text-gray-700">{items.join(' · ')}</p>
    </div>
  );
}

// GoodWork Events & Community Section
function GoodWorkCommunitySection({ place }: { place: Place }) {
  const goodworkEvents = place.goodworkEvents;
  if (!goodworkEvents) return null;

  const items: string[] = [];
  if (goodworkEvents.networking) items.push('Networking Events');
  if (goodworkEvents.workshops) items.push('Workshops');
  if (goodworkEvents.coworking) items.push('Coworking Sessions');

  const hasAnyContent = items.length > 0 || goodworkEvents.community;
  if (!hasAnyContent) return null;

  return (
    <div className="border-b border-gray-200 pb-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-3">Community & Events</h3>
      {items.length > 0 && (
        <p className="text-gray-700 mb-2">{items.join(' · ')}</p>
      )}
      {goodworkEvents.community && (
        <p className="text-gray-600 italic">"{goodworkEvents.community}"</p>
      )}
    </div>
  );
}

// Category Badge Component (supports both GoodLife and GoodWork categories)
function CategoryBadge({ category }: { category: AnyCategory }) {
  const categoryInfo = ALL_CATEGORIES.find((c) => c.id === category);
  if (!categoryInfo) return null;

  const colors = CATEGORY_COLORS[category];
  const IconComponent = ICON_MAP[categoryInfo.icon];

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${colors.bg} ${colors.text} ${colors.border}`}>
      {IconComponent && <IconComponent className="w-4 h-4" />}
      <span className="font-medium text-sm">{categoryInfo.label}</span>
    </div>
  );
}

// VibeTags Display Component
function VibeTagsDisplay({ vibeTags }: { vibeTags: string[] }) {
  const tagInfos = vibeTags
    .map((tagId) => VIBE_TAGS.find((t) => t.id === tagId))
    .filter(Boolean);

  if (tagInfos.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {tagInfos.map((tagInfo) => {
        if (!tagInfo) return null;
        const IconComponent = ICON_MAP[tagInfo.icon];
        return (
          <span
            key={tagInfo.id}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm"
          >
            {IconComponent && <IconComponent className="w-4 h-4" />}
            {tagInfo.label}
          </span>
        );
      })}
    </div>
  );
}

export function PlaceDetail({
  place,
  isFavorite,
  userId,
  userLocation,
  onBack,
  onToggleFavorite,
  onShowNotification,
}: PlaceDetailProps) {
  const { user } = useAuth();
  const [events, setEvents] = useState<PlaceEvent[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);

  const isOpen = place.openingHours ? isPlaceOpenNow(place.openingHours) : null;
  const valouAreas = getPlaceValouAreas(place);

  useEffect(() => {
    async function loadData() {
      setIsLoading(true);
      try {
        const [eventsData, reviewsData] = await Promise.all([
          getEventsForPlace(place.id),
          getReviewsForPlace(place.id),
        ]);
        setEvents(eventsData);
        setReviews(reviewsData);
      } catch (error) {
        console.error('Error loading place data:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [place.id]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: place.name,
          text: place.tagline,
          url: window.location.href,
        });
      } catch (error) {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(window.location.href);
      onShowNotification?.('Link kopiert!', 'success');
    }
  };

  const handleOpenMaps = () => {
    const query = encodeURIComponent(`${place.address}, ${place.city}`);
    window.open(`https://maps.google.com/maps?q=${query}`, '_blank');
  };

  const handleSaveNote = async (noteData: {
    text: string;
    personalRating?: number;
    visitDate?: Date;
    mood?: string;
  }) => {
    if (!user?.uid) {
      onShowNotification?.('Bitte melde dich an', 'info');
      return;
    }

    setIsSavingNote(true);
    try {
      await addPlaceNote({
        placeId: place.id,
        placeName: place.name,
        placeCategory: place.category,
        userId: user.uid,
        text: noteData.text,
        personalRating: noteData.personalRating,
        visitDate: noteData.visitDate,
        mood: noteData.mood,
      });
      onShowNotification?.('Notiz gespeichert!', 'success');
      setShowNoteEditor(false);
    } catch (error) {
      console.error('Error saving note:', error);
      onShowNotification?.('Fehler beim Speichern', 'error');
    } finally {
      setIsSavingNote(false);
    }
  };

  // Get images array
  const images = place.images?.length ? place.images : (place.imageUrl ? [place.imageUrl] : []);

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Back Button */}
            <button
              onClick={onBack}
              className={`p-2 rounded-full transition ${
                scrolled
                  ? 'hover:bg-gray-100'
                  : 'bg-white/90 backdrop-blur-sm shadow-sm hover:bg-white'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Title (visible when scrolled) */}
            <h1
              className={`font-semibold text-gray-900 transition-opacity ${
                scrolled ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {place.name}
            </h1>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleShare}
                className={`p-2 rounded-full transition ${
                  scrolled
                    ? 'hover:bg-gray-100'
                    : 'bg-white/90 backdrop-blur-sm shadow-sm hover:bg-white'
                }`}
              >
                <Share2 className="w-5 h-5" />
              </button>
              <button
                onClick={() => onToggleFavorite(place.id)}
                className={`p-2 rounded-full transition ${
                  scrolled
                    ? 'hover:bg-gray-100'
                    : 'bg-white/90 backdrop-blur-sm shadow-sm hover:bg-white'
                }`}
              >
                <Heart
                  className={`w-5 h-5 ${
                    isFavorite ? 'fill-primary text-indigo-600' : 'text-gray-600'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Image Gallery */}
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <ImageGallery images={images} placeName={place.name} />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Header */}
            <div className="border-b border-gray-200 pb-6 mb-6">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
                {place.name}
              </h1>
              {place.tagline && (
                <p className="text-lg text-indigo-600 italic mb-3">{place.tagline}</p>
              )}

              {/* Category Badge(s) - Show both for dual-realm places */}
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {/* Dual-Realm Indicator */}
                {place.realms?.includes('goodlife') && place.realms?.includes('goodwork') && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
                                bg-gradient-to-r from-emerald-500 to-indigo-500 text-white">
                    <Heart className="w-3.5 h-3.5" />
                    <span>+</span>
                    <Briefcase className="w-3.5 h-3.5" />
                    <span className="ml-1">Dual-Realm</span>
                  </div>
                )}

                {/* GoodLife Category */}
                {place.category && (
                  <CategoryBadge category={place.category} />
                )}

                {/* GoodWork Category */}
                {place.goodworkCategory && (
                  <CategoryBadge category={place.goodworkCategory} />
                )}
              </div>

              {/* Meta Bar */}
              <div className="flex items-center gap-3 flex-wrap text-sm">
                {/* Rating */}
                {place.rating > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                    <span className="font-semibold">{place.rating.toFixed(1)}</span>
                    <span className="text-gray-500">({place.reviewCount})</span>
                  </div>
                )}

                <span className="text-gray-300">|</span>

                {/* Location */}
                <span className="text-gray-600">{place.city || 'München'}</span>

                {/* Open Status */}
                {isOpen !== null && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className={`flex items-center gap-1 ${isOpen ? 'text-green-600' : 'text-gray-500'}`}>
                      <Clock className="w-4 h-4" />
                      {isOpen ? 'Jetzt geöffnet' : 'Geschlossen'}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* GoodFactor Box (GoodLife) */}
            {place.goodFactor && <GoodFactorBox goodFactor={place.goodFactor} />}

            {/* GoodWork Factor Box */}
            {place.goodworkFactor && <GoodWorkFactorBox goodworkFactor={place.goodworkFactor} />}

            {/* GoodWork Sections (only if this is a GoodWork place) */}
            {place.realms?.includes('goodwork') && (
              <>
                <GoodWorkAmenitiesSection place={place} />
                <GoodWorkPricingSection place={place} />
                <GoodWorkWorkspacesSection place={place} />
                <GoodWorkCommunitySection place={place} />
              </>
            )}

            {/* Pitch / Description */}
            <div className="border-b border-gray-200 pb-6 mb-6">
              <p className="text-gray-700 leading-relaxed text-lg">
                {place.pitch || place.description}
              </p>
              {/* Show description separately if both exist */}
              {place.pitch && place.description && place.pitch !== place.description && (
                <p className="text-gray-600 leading-relaxed mt-4">
                  {place.description}
                </p>
              )}
            </div>

            {/* Zusätzliche Informationen (aus KI-Verifikation) */}
            {place.verificationNotes && (
              <div className="border-b border-gray-200 pb-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Zusätzliche Informationen
                </h3>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {place.verificationNotes}
                </p>
              </div>
            )}

            {/* Vibe Tags */}
            {place.vibeTags && place.vibeTags.length > 0 && (
              <div className="border-b border-gray-200 pb-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Vibe & Charakter
                </h3>
                <VibeTagsDisplay vibeTags={place.vibeTags} />
              </div>
            )}

            {/* Valou Bereiche */}
            {valouAreas.length > 0 && (
              <div className="border-b border-gray-200 pb-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Gut für diese Lebensbereiche
                </h3>
                <ValouBadges areas={valouAreas} maxDisplay={6} size="sm" showLabels={true} />
              </div>
            )}

            {/* What awaits you */}
            {(place.moods.length > 0 || place.activities.length > 0) && (
              <div className="border-b border-gray-200 pb-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Was dich hier erwartet
                </h3>

                {/* Moods */}
                {place.moods.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-2">Atmosphäre</p>
                    <div className="flex flex-wrap gap-2">
                      {place.moods.map((mood) => (
                        <span
                          key={mood}
                          className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm"
                        >
                          {getMoodIcon(mood)} {mood}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Activities */}
                {place.activities.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Aktivitäten</p>
                    <div className="flex flex-wrap gap-2">
                      {place.activities.map((activity) => (
                        <span
                          key={activity}
                          className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm"
                        >
                          {getActivityIcon(activity)} {activity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Community Features */}
            {place.community.length > 0 && (
              <div className="border-b border-gray-200 pb-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5" /> Gemeinschaft
                </h3>
                <div className="flex flex-wrap gap-2">
                  {place.community.map((feature) => (
                    <span
                      key={feature}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm"
                    >
                      {getCommunityIcon(feature)} {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Events */}
            {events.length > 0 && (
              <div className="border-b border-gray-200 pb-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5" /> Regelmäßige Treffen & Events
                </h3>
                <div className="space-y-3">
                  {events.map((event) => (
                    <div
                      key={event.id}
                      className="flex items-center p-4 bg-gray-50 rounded-xl"
                    >
                      <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center text-white mr-4">
                        <Calendar className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{event.name}</div>
                        <div className="text-sm text-gray-600">
                          {event.day}, {event.time}
                          {event.recurring && (
                            <span className="ml-2 text-indigo-600">(regelmäßig)</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reviews Section */}
            <ReviewSection
              placeId={place.id}
              reviews={reviews}
              userId={userId}
              userName={user?.displayName || undefined}
              userProfilePicture={user?.photoURL || undefined}
              isLoading={isLoading}
              onReviewAdded={(review) => setReviews((prev) => [review, ...prev])}
              onShowNotification={onShowNotification}
            />
          </div>

          {/* Sidebar - Action Card */}
          <div className="lg:col-span-1 mt-8 lg:mt-0">
            <div className="sticky top-24 space-y-6">
              {/* Check-In Section */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-lg">
                <CheckInButton
                  placeId={place.id}
                  placeName={place.name}
                  userId={user?.uid}
                  userName={user?.displayName || undefined}
                  userPhoto={user?.photoURL || undefined}
                  onShowNotification={onShowNotification || (() => {})}
                  onAuthRequired={() => onShowNotification?.('Bitte melde dich an', 'info')}
                />
              </div>

              {/* Who's Here */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-lg">
                <CheckInList placeId={place.id} currentUserId={user?.uid} />
              </div>

              {/* Mini-Map with Route */}
              {place.latitude && place.longitude && (
                <PlaceMiniMap
                  latitude={place.latitude}
                  longitude={place.longitude}
                  name={place.name}
                  address={place.address}
                  city={place.city}
                  userLocation={userLocation}
                />
              )}

              {/* Info Card */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-lg">
                {/* Location */}
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Standort</h3>
                  <button
                    onClick={handleOpenMaps}
                    className="flex items-start gap-2 text-gray-600 hover:text-indigo-600 transition group w-full text-left"
                  >
                    <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span className="group-hover:underline">
                      {place.address}
                      {place.city && `, ${place.city}`}
                    </span>
                  </button>
                </div>

                {/* Opening Hours */}
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Öffnungszeiten</h3>
                  <div className="flex items-start gap-2 text-gray-600">
                    <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>{place.openingHours?.displayText || 'Keine Angabe'}</span>
                  </div>
                </div>

                {/* Contact */}
                {(place.phone || place.website || place.email) && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Kontakt</h3>
                    <div className="space-y-2">
                      {place.phone && (
                        <a
                          href={`tel:${place.phone}`}
                          className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition"
                        >
                          <Phone className="w-5 h-5" />
                          {place.phone}
                        </a>
                      )}
                      {place.website && (
                        <a
                          href={place.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition"
                        >
                          <Globe className="w-5 h-5" />
                          Website
                        </a>
                      )}
                      {place.email && (
                        <a
                          href={`mailto:${place.email}`}
                          className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition"
                        >
                          <Mail className="w-5 h-5" />
                          {place.email}
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={handleOpenMaps}
                    className="w-full py-3 bg-indigo-500 text-white rounded-lg font-semibold hover:bg-indigo-600 transition flex items-center justify-center gap-2"
                  >
                    <Navigation className="w-5 h-5" />
                    Route planen
                  </button>
                  <button
                    onClick={handleShare}
                    className="w-full py-3 border border-gray-900 text-gray-900 rounded-lg font-semibold hover:bg-gray-50 transition flex items-center justify-center gap-2"
                  >
                    <Share2 className="w-5 h-5" />
                    Teilen
                  </button>
                  <SuggestChangesButton
                    place={place}
                    userId={user?.uid}
                    userName={user?.displayName || undefined}
                    userEmail={user?.email || undefined}
                    onShowNotification={onShowNotification}
                    onAuthRequired={() => onShowNotification?.('Bitte melde dich an, um Änderungen vorzuschlagen', 'info')}
                  />
                  <button
                    onClick={() => {
                      if (!user?.uid) {
                        onShowNotification?.('Bitte melde dich an, um eine Notiz zu schreiben', 'info');
                        return;
                      }
                      setShowNoteEditor(true);
                    }}
                    className="w-full py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition flex items-center justify-center gap-2"
                  >
                    <BookOpen className="w-5 h-5" />
                    Notiz hinzufügen
                  </button>
                </div>

                {/* Note */}
                <p className="text-xs text-gray-500 text-center mt-4">
                  Dritte Orte leben vom Spontanen. Keine Reservierung nötig.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Fixed Action Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-30">
        <div className="flex gap-3">
          <button
            onClick={handleOpenMaps}
            className="flex-1 py-3 bg-indigo-500 text-white rounded-lg font-semibold hover:bg-indigo-600 transition flex items-center justify-center gap-2"
          >
            <Navigation className="w-5 h-5" />
            Route
          </button>
          <button
            onClick={handleShare}
            className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            <Share2 className="w-5 h-5" />
          </button>
          <button
            onClick={() => onToggleFavorite(place.id)}
            className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            <Heart
              className={`w-5 h-5 ${
                isFavorite ? 'fill-primary text-indigo-600' : 'text-gray-600'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Spacer for mobile action bar */}
      <div className="lg:hidden h-20" />

      {/* Note Editor Modal */}
      {showNoteEditor && user?.uid && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <NoteEditor
              placeId={place.id}
              placeName={place.name}
              placeCategory={place.category}
              userId={user.uid}
              onSave={handleSaveNote}
              onCancel={() => setShowNoteEditor(false)}
              isLoading={isSavingNote}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default PlaceDetail;
